/** @author PVCOMBANK */
import * as React from "react";
import { Checkbox, Paragraph, useTheme } from "react-native-paper";
import { RegisterOptions, useForm } from "react-hook-form";
import { Button } from "react-native-paper";
import { Block } from "@block";
import { useDesktopMediaQuery } from "@media-query";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import { InputText, InputTextCurrency, InputTextMultiline } from "app/shared/components/form/input";
import { Linking, StyleSheet } from "react-native";
import { ACCOUNT } from "app/shared/services/services-model.type";
import moment from "moment";
import {
  LINK_TERM,
  formatFdTerm,
  getIntRate,
  getTermDepositBranchName,
  getTermDepositProductName,
  loadBranchTermDeposit,
} from "app/modules/term-deposit/term-deposit-utils/term-deposit.utils";

export interface FormTermDepositStatementProps {
  onSubmit: Function;
  accountDebit: ACCOUNT;
  onCancel: Function;
  requestCreate: Function;
}

enum FormEnum {
  bookNo,
  productName,
  currentAmount,
  interestsRate,
  term,
  branchName,
  openDate,
  matDate,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export type FormTermDepositStatementData = {
  bookNo: string;
  productName: string;
  currentAmount: string;
  interestsRate: string;
  term: string;
  branchName: string;
  openDate: string;
  matDate: string;
};

const FormTermDepositStatement = (props: FormTermDepositStatementProps) => {
  const { onSubmit, onCancel, accountDebit, requestCreate } = props;
  const { colors, fonts } = useTheme();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();

  const [checked, setChecked] = React.useState<boolean>(false);

  // Instance Form Method
  const { control, setValue, getValues } = useForm<FormTermDepositStatementData>({
    defaultValues: {
      bookNo: "",
      productName: "",
      currentAmount: "",
      interestsRate: "",
      term: "",
      branchName: "",
      openDate: "",
      matDate: "",
    },
    mode: "onChange",
  });

  // Styles Component
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontSize: 16,
          ...fonts.semibold,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        acceptButtonContainer: { width: 128, alignSelf: "center", opacity: checked ? 1 : 0.5 },
        textTerm: { ...fonts.semibold, color: colors.text, fontSize: 15 },
        textTerm2: { ...fonts.regular, textDecorationLine: "underline", fontStyle: "italic" },
      }),
    [colors.text, colors.notification, fonts.semibold, fonts.regular, checked],
  );

  const onPressCreate = () => {
    onSubmit();
    requestCreate();
  };

  const onPressCancel = () => {
    onCancel();
  };

  const onClickShowTermLink = () => {
    Linking.openURL(LINK_TERM);
  };

  const FormCheckTerm = (
    <Block center={isDesktop ? true : false} flex={1} pt={spacing.medium}>
      <Block row>
        <StackSpacing size="medium" />
        <Checkbox
          status={checked ? "checked" : "unchecked"}
          onPress={() => {
            setChecked(!checked);
          }}
        />
        <Paragraph style={styles.textTerm}>
          {t("SAVING_NOTE_DIEU_KHOAN_DIEU_KIEN_1")}{" "}
          <Paragraph style={[styles.textTerm, styles.textTerm2]} onPress={onClickShowTermLink}>
            {t("SAVING_NOTE_DIEU_KHOAN_DIEU_KIEN_2")}
          </Paragraph>{" "}
          {t("SAVING_NOTE_DIEU_KHOAN_DIEU_KIEN_3")}
        </Paragraph>
        <StackSpacing size="medium" />
      </Block>
    </Block>
  );

  /// MARK: - Child Component
  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("SAVING_THONG_TIN_SO_TIET_KIEM")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="productName" label={t("SAVING_SAN_PHAM")} />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputTextCurrency
            name="currentAmount"
            inputProps={{ editable: false }}
            control={control}
            label={t("SAVING_SO_DU_SO")}
            rightCurrency={t("COMMON_VND")}
          />
        </Block>
      </Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name={"term"} label={t("SAVING_KY_HAN")} />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="interestsRate" label={t("SAVING_LAI_SUAT")} />
        </Block>
      </Block>
      <Block spaced={isDesktop} row={isDesktop}>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="openDate" label={t("SAVING_NGAY_MO_SO")} />
        </Block>
        <Block w={isDesktop ? "45%" : "100%"}>
          <InputText control={control} inputProps={{ editable: false }} name="matDate" label={t("SAVING_NGAY_DEN_HAN")} />
        </Block>
      </Block>
      <InputTextMultiline control={control} inputProps={{ editable: false }} name="branchName" label={t("SAVING_CHI_NHANH_QUAN_LY_SO")} />
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button mode="contained" style={styles.buttonContainer} onPress={onPressCancel}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate} disabled={!checked}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    if (accountDebit.ACCOUNTNO?.length && accountDebit.ACCOUNTNO !== getValues("bookNo")) {
      setValue("bookNo", accountDebit.ACCOUNTNO || "");
      setValue("productName", getTermDepositProductName(accountDebit.PRODUCTCODE));
      setValue("currentAmount", accountDebit.CURRENTBALANCE || "0");
      loadBranchTermDeposit().then(() => {
        setValue("branchName", getTermDepositBranchName(accountDebit.BRANCH_CODE) || "");
      });
      setValue("interestsRate", getIntRate(accountDebit.INTRATE) + " % / " + t("COMMON_NAM").toLowerCase());
      setValue("term", formatFdTerm(accountDebit.FDTERM));
      setValue("openDate", moment(String(accountDebit.OPENDATE)).format("DD/MM/YYYY").toString());
      setValue("matDate", moment(String(accountDebit.MATDATE)).format("DD/MM/YYYY").toString());
    }
  }, [accountDebit, setValue, getValues, t]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        <Block w={200} />
        {FormCheckTerm}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormTermDepositStatement;
